import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, ArrowLeft, Globe, MapPin, Target, Users } from 'lucide-react';

const BUSINESS_TYPES = ['Pizzeria', 'Restaurant', 'Cafe', 'Bakery', 'Bar & Grill', 'Food Truck', 'Deli', 'Other'];
const TIME_HORIZONS = [{ value: 30, label: '30 days' }, { value: 60, label: '60 days' }, { value: 90, label: '90 days' }];

export default function ProjectSetupPage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const [form, setForm] = useState({
    business_name: '',
    business_type: 'Pizzeria',
    address: '',
    city: '',
    state: '',
    country: 'US',
    phone: '',
    service_area_radius: 5,
    neighborhoods: '',
    website_url: '',
    specific_url: '',
    gbp_url: '',
    intention: '',
    time_horizon: 90,
    competitors: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const steps = [
    { icon: Globe, title: 'Business Identity', desc: 'Tell us about your business' },
    { icon: MapPin, title: 'Location & Assets', desc: 'Your website and online presence' },
    { icon: Target, title: 'Your Intention', desc: 'What do you want to achieve?' },
    { icon: Users, title: 'Competition', desc: 'Who are you competing with?' }
  ];

  const canProceed = () => {
    if (step === 0) return form.business_name && form.business_type;
    if (step === 1) return form.website_url;
    if (step === 2) return form.intention;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        neighborhoods: form.neighborhoods.split(',').map(s => s.trim()).filter(Boolean),
        competitors: form.competitors.split('\n').map(s => s.trim()).filter(Boolean),
        service_area_radius: parseInt(form.service_area_radius) || 5,
        time_horizon: parseInt(form.time_horizon) || 90
      };
      const { data } = await api.post('/projects', payload);
      navigate(`/projects/${data.id}/goals`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${i <= step ? 'bg-[#0268C4] text-white' : 'bg-[#EFF3F8] text-[#64748B]'}`}>
                {i + 1}
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 transition-all ${i < step ? 'bg-[#0268C4]' : 'bg-[#E2E8F0]'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_4px_24px_rgba(44,48,46,0.04)] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            {(() => { const Icon = steps[step].icon; return <div className="w-10 h-10 bg-[#0268C4]/10 rounded-xl flex items-center justify-center"><Icon className="w-5 h-5 text-[#0268C4]" strokeWidth={1.5} /></div>; })()}
            <div>
              <h2 className="font-heading text-xl font-medium text-[#0F2951]">{steps[step].title}</h2>
              <p className="text-sm text-[#64748B]">{steps[step].desc}</p>
            </div>
          </div>

          {error && <div className="mb-4 p-3 bg-[#EF4444]/10 rounded-xl text-[#EF4444] text-sm">{error}</div>}

          {step === 0 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Business Name *</Label>
                <Input data-testid="setup-business-name" value={form.business_name} onChange={e => update('business_name', e.target.value)} placeholder="Tony's Pizza" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Business Type *</Label>
                <Select value={form.business_type} onValueChange={v => update('business_type', v)}>
                  <SelectTrigger data-testid="setup-business-type" className="border-[#E2E8F0] bg-white rounded-xl h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{BUSINESS_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Phone</Label>
                  <Input data-testid="setup-phone" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(555) 123-4567" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Service Radius (mi)</Label>
                  <Input data-testid="setup-radius" type="number" value={form.service_area_radius} onChange={e => update('service_area_radius', e.target.value)} className="border-[#E2E8F0] bg-white rounded-xl h-12" />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Street Address</Label>
                <Input data-testid="setup-address" value={form.address} onChange={e => update('address', e.target.value)} placeholder="123 Main St" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">City</Label>
                  <Input data-testid="setup-city" value={form.city} onChange={e => update('city', e.target.value)} placeholder="San Diego" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">State</Label>
                  <Input data-testid="setup-state" value={form.state} onChange={e => update('state', e.target.value)} placeholder="CA" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Website URL *</Label>
                <Input data-testid="setup-website-url" value={form.website_url} onChange={e => update('website_url', e.target.value)} placeholder="https://tonys-pizza.com" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Specific URL to Audit (optional)</Label>
                <Input data-testid="setup-specific-url" value={form.specific_url} onChange={e => update('specific_url', e.target.value)} placeholder="https://tonys-pizza.com/menu" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Google Business Profile URL (optional)</Label>
                <Input data-testid="setup-gbp-url" value={form.gbp_url} onChange={e => update('gbp_url', e.target.value)} placeholder="https://business.google.com/..." className="border-[#E2E8F0] bg-white rounded-xl h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Neighborhoods (comma-separated)</Label>
                <Input data-testid="setup-neighborhoods" value={form.neighborhoods} onChange={e => update('neighborhoods', e.target.value)} placeholder="Little Italy, Hillcrest, North Park" className="border-[#E2E8F0] bg-white rounded-xl h-12" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Intention Statement *</Label>
                <p className="text-sm text-[#64748B]">Your North Star — what do you want to achieve?</p>
                <Textarea
                  data-testid="setup-intention"
                  value={form.intention}
                  onChange={e => update('intention', e.target.value)}
                  placeholder="Rank top 3 for 'pizza near me' and 'best pizza in San Diego' within 5 miles, and increase direct online orders."
                  rows={4}
                  className="border-[#E2E8F0] bg-white rounded-xl resize-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Time Horizon</Label>
                <Select value={String(form.time_horizon)} onValueChange={v => update('time_horizon', v)}>
                  <SelectTrigger data-testid="setup-time-horizon" className="border-[#E2E8F0] bg-white rounded-xl h-12"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_HORIZONS.map(t => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Top Competitors</Label>
                <p className="text-sm text-[#64748B]">One per line — business names or URLs (optional, up to 10)</p>
                <Textarea
                  data-testid="setup-competitors"
                  value={form.competitors}
                  onChange={e => update('competitors', e.target.value)}
                  placeholder={"Bronx Pizza\nFilippi's Pizza Grotto\nBasic Bar & Pizza"}
                  rows={5}
                  className="border-[#E2E8F0] bg-white rounded-xl resize-none"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
            <Button
              data-testid="setup-back-button"
              variant="ghost"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 0}
              className="text-[#64748B] hover:text-[#0F2951] rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>

            {step < 3 ? (
              <Button
                data-testid="setup-next-button"
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
                className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 transition-all duration-200 hover:-translate-y-[1px]"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="setup-create-button"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 transition-all duration-200 hover:-translate-y-[1px]"
              >
                {loading ? 'Creating...' : 'Create Project'}
                {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
